// Controlled number input with optional thousands-separator formatting for
// currency fields. Same behavior as the Seller Net Sheet NumField —
// deliberately duplicated per the "no cross-page imports" rule.

import React, { useEffect, useState } from 'react';
import HelpTip from './HelpTip';

const withCommas = (intDigits) =>
  intDigits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

function formatDisplay(value, isCurrency) {
  if (value == null || value === 0 || Number.isNaN(value)) return '';
  if (!isCurrency) return String(value);
  const str = String(value);
  const neg = str.startsWith('-');
  const body = neg ? str.slice(1) : str;
  const [intPart, decPart] = body.split('.');
  const out = withCommas(intPart || '0') + (decPart !== undefined ? '.' + decPart : '');
  return neg ? '-' + out : out;
}

function formatTyping(stripped) {
  if (stripped === '' || stripped === '-' || stripped === '.' || stripped === '-.') return stripped;
  const neg = stripped.startsWith('-');
  const body = neg ? stripped.slice(1) : stripped;
  const dotIdx = body.indexOf('.');
  if (dotIdx === -1) return (neg ? '-' : '') + withCommas(body);
  return (neg ? '-' : '') + withCommas(body.slice(0, dotIdx) || '0') + '.' + body.slice(dotIdx + 1);
}

function useNumberInput(value, onChange, isCurrency) {
  const [raw, setRaw] = useState(() => formatDisplay(value, isCurrency));

  useEffect(() => {
    const parsed = parseFloat(raw.replace(/,/g, ''));
    const matches = Number.isNaN(parsed) ? (value === 0 || value == null) : parsed === value;
    if (!matches) setRaw(formatDisplay(value, isCurrency));
  }, [value, isCurrency]);

  const handleChange = (e) => {
    const stripped = e.target.value.replace(/,/g, '');
    if (stripped !== '' && !/^-?\d*\.?\d*$/.test(stripped)) return;
    setRaw(isCurrency ? formatTyping(stripped) : stripped);
    if (stripped === '' || stripped === '-' || stripped === '.' || stripped === '-.') {
      onChange(0);
      return;
    }
    const n = parseFloat(stripped);
    if (!Number.isNaN(n)) onChange(n);
  };

  const handleBlur = () => {
    const stripped = raw.replace(/,/g, '');
    if (stripped === '' || stripped === '-' || stripped === '.') {
      setRaw('');
      onChange(0);
      return;
    }
    const n = parseFloat(stripped);
    if (!Number.isNaN(n)) setRaw(formatDisplay(n, isCurrency));
  };

  return { raw, handleChange, handleBlur };
}

export default function NumField({
  label, value, onChange, prefix, suffix, help, placeholder, testId,
}) {
  const isCurrency = prefix === '$';
  const { raw, handleChange, handleBlur } = useNumberInput(value, onChange, isCurrency);
  return (
    <label className="block text-sm">
      <span className="text-[#092825] font-medium inline-flex items-center">
        {label}
        {help && <HelpTip text={help} />}
      </span>
      <div className="mt-1 relative">
        {prefix && (
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">{prefix}</span>
        )}
        <input
          type="text"
          inputMode="decimal"
          placeholder={placeholder || '0'}
          data-testid={testId}
          className={
            'ida-num w-full rounded border border-slate-300 focus:border-[#2FA163] focus:ring-1 focus:ring-[#2FA163] outline-none py-2 ' +
            (prefix ? 'pl-7' : 'pl-3') + ' ' +
            (suffix ? 'pr-10' : 'pr-3') + ' text-sm bg-white'
          }
          value={raw}
          onChange={handleChange}
          onBlur={handleBlur}
        />
        {suffix && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">{suffix}</span>
        )}
      </div>
    </label>
  );
}
